<template>
  <component :is="MarketCard" :marketId="marketID" :eventId="eventID" />
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, computed, ref } from "vue";
import { useBetData } from "../hooks/useBetData";
import { useDisplayEvents } from "../hooks/useDisplayingEvents";
import { composeEventMarkets, composeEvent } from "../composition/betData";
import { Market } from "../types/betData";
import {
  LISTS,
  MAIN_MARKETS,
  FULL_TIME_RESULT,
  PROMOS
} from "../constants/market-types";

/**
 * Checks if event can display main market markets
 */
const isMultiFtr = (eventMarkets: Market[]) => {
  const ftrMarkets = eventMarkets.filter(market =>
    MAIN_MARKETS.includes(market.marketType)
  );
  return ftrMarkets.length > 1;
};

/**
 * Checks if market display a draw option
 */
const isFtr = (eventMarket: Market) => {
  return eventMarket.options.length > 2;
};

/**
 * Gets the appropriate ftr card for the events ftr markets
 */
const getFtrCard = eventMarkets =>
  isMultiFtr(eventMarkets)
    ? defineAsyncComponent(() => import(`./MultiFtr.vue`))
    : isFtr(eventMarkets[0])
    ? defineAsyncComponent(() => import(`./FullTimeResult.vue`))
    : defineAsyncComponent(() => import(`./MatchWinner.vue`));

const MARKET_CARD_MAP = {
  default: defineAsyncComponent(() => import(`./List.vue`))
};

MARKET_CARD_MAP[FULL_TIME_RESULT] = getFtrCard;

MAIN_MARKETS.forEach(market => {
  MARKET_CARD_MAP[market] = getFtrCard;
});

LISTS.forEach(market => {
  MARKET_CARD_MAP[market] = defineAsyncComponent(() => import(`./List.vue`));
});

PROMOS.forEach(market => {
  MARKET_CARD_MAP[market] = defineAsyncComponent(() => import(`./Promo.vue`));
});

export default defineComponent({
  name: "CardSelector",
  props: {
    eventId: String,
    marketIds: Array
  },
  setup(props) {
    const { markets } = useBetData();
    const { eventsOrderbyDate } = useDisplayEvents();

    const event = computed(() => {
      if (props.eventId) {
        return composeEvent(props.eventId).value;
      }
      return composeEvent(eventsOrderbyDate.value[0].id).value;
    });

    const eventMarkets = computed(() => {
      const marketIds = props.marketIds
        ? ref(props.marketIds)
        : composeEventMarkets(event.value.id);
      return marketIds.value.map((id: string) => markets.value[id]);
    });

    const MarketCard = computed(() => {
      const marketCardSetter =
        MARKET_CARD_MAP[eventMarkets.value[0].marketType] ||
        MARKET_CARD_MAP.default;

      if (typeof marketCardSetter === "function") {
        return marketCardSetter(eventMarkets.value);
      } else {
        return marketCardSetter;
      }
    });

    return {
      MarketCard,
      marketID: computed(() => eventMarkets.value[0].marketId),
      eventID: computed(() => eventMarkets.value[0].eventId)
    };
  }
});
</script>
